// ─────────────────────────────────────────────
//  TipCalculator.jsx — Calculadora de Propinas
//  Extracted from App.jsx — logic and JSX unchanged
// ─────────────────────────────────────────────

import React, { useState, useCallback, useEffect } from 'react';
import styles from '../layout/CalculatorPage.module.css';
import { KEYS, getSetting } from '../../utils/storage';
import { DEFAULTS } from '../../utils/defaults';

import AmountInput from './AmountInput';
import TipSlider from './TipSlider';
import PeoplePicker from './PeoplePicker';
import Summary from './Summary';
import PerPerson from './PerPerson';
import ToggleSwitch from './ToggleSwitch';

const DEFAULT_PEOPLE = 1;
const QUICK_TIPS = [10, 15, 20, 40, 50];
const SLIDER_MIN = 5;
const SLIDER_MAX = 30;
const DEFAULT_ROUND_CONSUMPTION = true;
const DEFAULT_ROUND_PER_PERSON = false;

function getDefaultTip() {
	return getSetting(KEYS.defaultTip, DEFAULTS.defaultTip);
}

const TOGGLE_COLORS = {
	checkedBackground: '#0cbdb1',
	checkedBorderColor: 'rgba(67, 67, 67, 0.99)',
	checkedThumbColor: '#e8fff9',
	focusOutlineColor: 'rgba(168, 230, 207, 0.55)',
};

export default function TipCalculator() {
	const [amount, setAmount] = useState('');
	const [tipPct, setTipPct] = useState(getDefaultTip);
	const [people, setPeople] = useState(DEFAULT_PEOPLE);
	const [roundConsumption, setRoundConsumption] = useState(DEFAULT_ROUND_CONSUMPTION);
	const [roundPerPerson, setRoundPerPerson] = useState(DEFAULT_ROUND_PER_PERSON);

	// ── Derived values ──────────────────────────
	const parsedAmount = parseFloat(amount) || 0;
	const hasAmount = parsedAmount > 0;

	let effectiveAmount = parsedAmount;
	let tipAmount = 0;
	let total = 0;
	let perAmount = 0;
	let perTip = 0;

	if (hasAmount) {
		const baseAmount =
			roundConsumption ? Math.ceil(parsedAmount) : parsedAmount;
		const baseTip = roundConsumption
			? Math.ceil(baseAmount * (tipPct / 100))
			: baseAmount * (tipPct / 100);

		if (roundPerPerson && people > 1) {
			perAmount = Math.ceil(baseAmount / people);
			perTip = Math.ceil(baseTip / people);
			effectiveAmount = perAmount * people;
			tipAmount = perTip * people;
		} else {
			effectiveAmount = baseAmount;
			tipAmount = baseTip;
			perAmount = effectiveAmount / people;
			perTip = tipAmount / people;
		}

		total = effectiveAmount + tipAmount;
	}

	const useIntegerAmounts =
		hasAmount && (roundConsumption || (roundPerPerson && people > 1));

	const showPerPersonBreakdown = hasAmount && people > 1;

	useEffect(() => {
		document.body.classList.toggle('scroll-y', showPerPersonBreakdown);
		return () => document.body.classList.remove('scroll-y');
	}, [showPerPersonBreakdown]);

	// ── Handlers ───────────────────────────────
	const handleReset = useCallback(() => {
		setAmount('');
		setTipPct(getDefaultTip());
		setPeople(DEFAULT_PEOPLE);
		setRoundConsumption(DEFAULT_ROUND_CONSUMPTION);
		setRoundPerPerson(DEFAULT_ROUND_PER_PERSON);
	}, []);

	const handlePeople = useCallback((delta) => {
		setPeople((prev) => Math.max(1, Math.min(20, prev + delta)));
	}, []);

	return (
		<main className={styles.root} aria-label="Calculadora de propinas">

			{/* ── Top Bar ── */}
			<div className={styles.topBar}>
				<div className={styles.switchRow}>
					<ToggleSwitch
						label="Redondear Total"
						checked={roundConsumption}
						onChange={setRoundConsumption}
						ariaLabel="Redondear consumo hacia arriba"
						colors={TOGGLE_COLORS}
					/>
				</div>
			</div>

			{/* ── Amount Input ── */}
			<AmountInput value={amount} onChange={setAmount} onReset={handleReset} />

			{/* ── Tip Slider + Quick Buttons ── */}
			<TipSlider
				value={tipPct}
				onChange={setTipPct}
				min={SLIDER_MIN}
				max={SLIDER_MAX}
				quickTips={QUICK_TIPS}
				tipAmount={hasAmount ? tipAmount : null}
			/>

			{/* ── Summary ── */}
			<Summary
				amount={effectiveAmount}
				tipPct={tipPct}
				tipAmount={tipAmount}
				total={total}
				hasAmount={hasAmount}
				useIntegerAmounts={useIntegerAmounts}
			/>

			{/* ── People Picker ── */}
			<PeoplePicker
				people={people}
				onChange={handlePeople}
			/>

			{/* ── Per Person breakdown ── */}
			<PerPerson
				total={total}
				amount={effectiveAmount}
				tipAmount={tipAmount}
				perAmount={perAmount}
				perTip={perTip}
				people={people}
				hasAmount={hasAmount}
				tipPct={tipPct}
				useIntegerAmounts={useIntegerAmounts}
				roundPerPerson={roundPerPerson}
				onRoundPerPersonChange={setRoundPerPerson}
			/>

		</main>
	);
}
